import React, { useState, useMemo, useEffect } from 'react';
import { 
  Card, 
  Button, 
  Space, 
  Modal, 
  Form, 
  Input, 
  Select, 
  message,
  Typography,
  Tooltip,
  Popconfirm,
  Alert
} from 'antd';
import { 
  ApartmentOutlined, 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import CMTStructureVisualization from '../shared/CMTStructureVisualization';

const { Title, Text } = Typography;
const { Option } = Select;

/**
 * 🏗️ CMT Structure Manager
 * 
 * Allows Super Admins to manage CMT hierarchy structure:
 * - View hierarchical tree
 * - Add new CMT layers
 * - Edit existing CMTs
 * - Delete CMTs (with warnings)
 */
/**
 * 📏 Capitalize first letter of string
 */
const capitalizeFirst = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/**
 * 🏷️ Determine CMT type based on CMT data
 */
const getCMTType = (cmt) => {
  if (cmt.isGlobalCmt || cmt.level === 0) return 'Global';
  if (cmt.type) return capitalizeFirst(cmt.type);
  if (cmt.level === 1) return 'Regional';
  if (cmt.level === 2) return 'Country';
  if (cmt.level === 3) return 'Site';
  return 'Regional'; // Default fallback
};

/**
 * 📝 Generate default scope text based on CMT data
 */
const getDefaultScope = (cmt) => {
  const type = getCMTType(cmt);
  const defaults = {
    'Global': 'Organization-wide crisis management and coordination',
    'Regional': 'Regional crisis response and coordination',
    'Country': 'Country-specific crisis management',
    'Site': 'Site-level emergency response'
  };
  return defaults[type] || 'Crisis management responsibilities';
};

/**
 * 🔄 Rebuild hierarchy data structure from flat CMTs array - FOR IMMEDIATE VISUAL UPDATE
 */
const rebuildHierarchyFromCmts = (cmtsArray) => {
  if (!cmtsArray || cmtsArray.length === 0) return null;
  
  // Find root CMT (one with no parentId)
  const rootCmt = cmtsArray.find(cmt => !cmt.parentId);
  if (!rootCmt) return null;
  
  // Recursive function to build tree
  const buildTree = (parentId) => {
    return cmtsArray
      .filter(cmt => cmt.parentId === parentId)
      .map(cmt => ({
        id: cmt.id,
        name: cmt.name,
        type: getCMTType(cmt),
        scope: cmt.description || cmt.scope || getDefaultScope(cmt),
        children: buildTree(cmt.id)
      }));
  };
  
  return {
    root: {
      id: rootCmt.id,
      name: rootCmt.name,
      type: getCMTType(rootCmt),
      scope: rootCmt.description || rootCmt.scope || getDefaultScope(rootCmt),
      children: buildTree(rootCmt.id)
    }
  };
};

export default function CMTStructureManager({ cmts, onStructureChange }) {
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingCmt, setEditingCmt] = useState(null);
  const [form] = Form.useForm();
  
  // 🚨 LOCAL STATE FOR IMMEDIATE VISUAL UPDATES
  const [localCmts, setLocalCmts] = useState(cmts || []);
  const [hierarchyData, setHierarchyData] = useState(null);
  const [renderKey, setRenderKey] = useState(0);
  
  // Update local state when props change (ONLY ONCE)
  useEffect(() => {
    setLocalCmts(cmts || []);
  }, [cmts]);
  
  // Regenerate hierarchyData whenever localCmts changes
  useEffect(() => {
    const newHierarchy = rebuildHierarchyFromCmts(localCmts);
    setHierarchyData(newHierarchy);
  }, [localCmts]);
  
  // Force re-render when hierarchyData changes
  useEffect(() => {
    setRenderKey(prev => prev + 1);
  }, [hierarchyData]);

  /**
   * 🧹 Clean CMT data for safe database save (GPT Solution)
   * Removes React Flow functions while preserving all required database fields
   */
  const cleanCmtForSave = (cmt) => {
    // Smart clone that preserves Firestore Timestamps and Dates
    const clone = { ...cmt };
    
    // Handle nested objects (permissions, metadata) with shallow clone to avoid reference issues
    if (clone.permissions && typeof clone.permissions === 'object') {
      clone.permissions = { ...clone.permissions };
    }
    if (clone.metadata && typeof clone.metadata === 'object') {
      clone.metadata = { ...clone.metadata };
    }
    if (clone.childCmtIds && Array.isArray(clone.childCmtIds)) {
      clone.childCmtIds = [...clone.childCmtIds];
    }
    
    // Remove ONLY React Flow contamination
    delete clone.onEdit;
    delete clone.onDelete;
    delete clone.onAddChild;
    delete clone.showEditControls;

    // Fix field name mismatch: parentId → parentCmtId
    if (clone.parentId && !clone.parentCmtId) {
      clone.parentCmtId = clone.parentId;
      delete clone.parentId;
    }

    // Ensure required fields exist with sensible defaults
    const requiredFields = {
      'permissions': {},
      'metadata': {},
      'childCmtIds': [],
      'isGlobalCmt': false,
      'isDefault': false,
      'isActive': true,
      'members': []
    };

    Object.entries(requiredFields).forEach(([field, defaultValue]) => {
      if (!(field in clone)) {
        console.warn(`Missing required field: ${field} in CMT ${clone.id}, adding default`);
        clone[field] = defaultValue;
      }
    });

    return clone;
  };

  /**
   * 🏗️ Convert flat CMT array to hierarchical structure for visualization - REMOVED DUPLICATE
   */
  const memoHierarchyData = useMemo(() => {
    console.log('🔄 useMemo CALLED - localCmts length:', localCmts.length, 'localCmts:', localCmts.map(c => ({ id: c.id, name: c.name, parentCmtId: c.parentCmtId })));
    
    if (!localCmts || localCmts.length === 0) {
      return null;
    }

    // Find root CMT (Global or no parent)
    const rootCMT = localCmts.find(cmt => 
      cmt.isGlobalCmt || 
      !cmt.parentCmtId || 
      cmt.level === 0 ||
      cmt.type === 'global'
    );

    if (!rootCMT) {
      // Fallback: use first CMT as root
      const fallbackRoot = localCmts[0];
      return {
        root: {
          id: fallbackRoot.id,
          name: fallbackRoot.name || 'Crisis Management Team',
          type: 'Global',
          scope: fallbackRoot.description || 'Organization-wide crisis management',
          children: []
        }
      };
    }

    // Build hierarchy recursively
    const buildHierarchy = (parentCmt) => {
      console.log('🔍 buildHierarchy called for:', { 
        parentId: parentCmt.id, 
        parentName: parentCmt.name,
        totalLocalCmts: localCmts.length,
        localCmtsIds: localCmts.map(c => ({ id: c.id, name: c.name, parentCmtId: c.parentCmtId }))
      });
      
      const children = localCmts.filter(cmt => cmt.parentCmtId === parentCmt.id);
      console.log('🔍 Found children for', parentCmt.name + ':', children.map(c => ({ id: c.id, name: c.name, parentCmtId: c.parentCmtId })));
      
      const childrenHierarchy = children.map(child => buildHierarchy(child));
      console.log('🔍 Built children hierarchy for', parentCmt.name + ':', childrenHierarchy);
      
      const result = {
        id: parentCmt.id,
        name: parentCmt.name || 'Crisis Management Team',
        type: getCMTType(parentCmt),
        scope: parentCmt.description || getDefaultScope(parentCmt),
        children: childrenHierarchy
      };
      
      console.log('🔍 Final result for', parentCmt.name + ':', result);
      return result;
    };

    const hierarchyResult = buildHierarchy(rootCMT);
    console.log('🎯 useMemo FINAL RETURN - hierarchyResult:', JSON.stringify(hierarchyResult, null, 2));
    
    return {
      root: hierarchyResult
    };
  }, [localCmts]);

  /**
   * 🆕 Handle adding new root CMT
   */
  const handleAddRoot = () => {
    setEditingCmt(null);
    form.resetFields();
    form.setFieldsValue({
      type: 'global',
      parentId: null
    });
    setEditModalVisible(true);
  };

  /**
   * 👶 Handle adding child CMT
   */
  const handleAddChild = (parentId) => {
    setEditingCmt(null);
    form.resetFields();
    form.setFieldsValue({
      type: 'regional',
      parentId: parentId
    });
    setEditModalVisible(true);
  };

  /**
   * ✏️ Handle editing existing CMT
   */
  const handleEdit = (cleanCmtData) => {
    // 🧹 Find full CMT data from localCmts (avoid contaminated React Flow data)
    const fullCmtData = localCmts.find(cmt => cmt.id === cleanCmtData.id);
    if (!fullCmtData) {
      message.error('CMT not found');
      return;
    }
    
    setEditingCmt(fullCmtData);
    form.setFieldsValue({
      name: fullCmtData.name,
      type: fullCmtData.type,
      parentId: fullCmtData.parentId,
      description: fullCmtData.description || ''
    });
    setEditModalVisible(true);
  };

  /**
   * 🗑️ Handle deleting CMT
   */
  const handleDelete = (cmtId) => {
    setLocalCmts(prev => {
      const updated = prev.filter(cmt => {
        // Remove the CMT and all its descendants
        const isDescendant = (id, parentId) => {
          if (id === parentId) return true;
          const parent = prev.find(c => c.id === id);
          if (parent && parent.parentId) {
            return isDescendant(parent.parentId, parentId);
          }
          return false;
        };
        return !isDescendant(cmt.id, cmtId);
      });
      
      // Clean CMT data before passing to parent (GPT solution)
      const cleanedCmts = updated.map(cleanCmtForSave);
      
      // Notify parent component of changes
      onStructureChange(cleanedCmts);
      return updated;
    });
    
    message.success('CMT and all child CMTs deleted successfully');
    // Force visual re-render
    setRenderKey(prev => prev + 1);
  };

  /**
   * 💾 Handle save CMT (add or edit)
   */
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      
      if (editingCmt) {
        // Update existing CMT
        setLocalCmts(prev => {
          const updated = prev.map(cmt => 
            cmt.id === editingCmt.id 
              ? { ...cmt, ...values }
              : cmt
          );
          
          // Clean CMT data before passing to parent (GPT solution)
          const cleanedCmts = updated.map(cleanCmtForSave);
          
          // Notify parent component of changes
          onStructureChange(cleanedCmts);
          return updated;
        });
        
        message.success('CMT updated successfully');
        // Force visual re-render
        setRenderKey(prev => prev + 1);
      } else {
        // Add new CMT
        const newCmt = {
          id: `cmt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          ...values,
          createdAt: new Date().toISOString(),
          createdBy: 'admin_user'
        };
        
        setLocalCmts(prev => {
          const updated = [...prev, newCmt];
          
          // Clean CMT data before passing to parent (GPT solution)
          const cleanedCmts = updated.map(cleanCmtForSave);
          
          // Notify parent component of changes
          onStructureChange(cleanedCmts);
          return updated;
        });
        
        message.success('CMT added successfully');
        // Force visual re-render
        setRenderKey(prev => prev + 1);
      }
      
      setEditModalVisible(false);
      form.resetFields();
      setEditingCmt(null);
      
    } catch (error) {
      console.error('Error saving CMT:', error);
      message.error('Failed to save CMT');
    }
  };

  return (
    <div>
      <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={4} style={{ margin: 0 }}>
          <ApartmentOutlined style={{ marginRight: '8px' }} />
          CMT Structure
        </Title>
        <Space>
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={handleAddRoot}
          >
            Add Root CMT
          </Button>
        </Space>
      </div>

      {/* Interactive Visual Hierarchy Display */}
      {memoHierarchyData ? (
        <Card style={{ marginBottom: '16px', padding: '0', overflow: 'hidden' }}>
          {(() => {
            console.log('🎯 JUST BEFORE CMTStructureVisualization - memoHierarchyData:', JSON.stringify(memoHierarchyData, null, 2));
            return null;
          })()}
          <CMTStructureVisualization 
            hierarchyData={memoHierarchyData}
            readOnly={false}
            showEditControls={true}
            onNodeEdit={handleEdit}
            onNodeDelete={handleDelete}
            onNodeAddChild={handleAddChild}
            key={renderKey}
          />
        </Card>
      ) : (
        <Card style={{ minHeight: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Alert
            message="No CMT Structure Found"
            description="This organization doesn't have any CMTs configured yet. Add a root CMT to get started."
            type="info"
            showIcon
            icon={<ExclamationCircleOutlined />}
            action={
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAddRoot}>
                Add First CMT
              </Button>
            }
          />
        </Card>
      )}

      {/* Edit/Add CMT Modal */}
      <Modal
        title={editingCmt ? 'Edit CMT' : 'Add New CMT'}
        open={editModalVisible}
        onOk={handleSave}
        onCancel={() => {
          setEditModalVisible(false);
          form.resetFields();
          setEditingCmt(null);
        }}
        okText={editingCmt ? 'Update' : 'Add'}
        cancelText="Cancel"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="CMT Name"
            rules={[{ required: true, message: 'Please enter CMT name' }]}
          >
            <Input placeholder="e.g., Global CMT, APAC Regional CMT" />
          </Form.Item>

          <Form.Item
            name="type"
            label="CMT Type"
            rules={[{ required: true, message: 'Please select CMT type' }]}
          >
            <Select placeholder="Select CMT type">
              <Option value="global">Global</Option>
              <Option value="regional">Regional</Option>
              <Option value="country">Country</Option>
              <Option value="site">Site</Option>
              <Option value="functional">Functional</Option>
              <Option value="division">Division</Option>
              <Option value="custom">Custom</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="description"
            label="Description (Optional)"
          >
            <Input.TextArea 
              rows={3} 
              placeholder="Brief description of this CMT's scope and responsibilities" 
            />
          </Form.Item>

          <Form.Item name="parentId" hidden>
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

